// src/components/sponsorships/Sponsorships.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  Sponsorships — "TelcoFair Glass Workspace" with Enhanced Animations
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Alert,
    Snackbar,
    Card,
    CardContent,
    Avatar,
    IconButton,
    Chip,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    LinearProgress,
    Stack,
    Tabs,
    Tab,
    CircularProgress,
    Divider,
    Slider,
    FormGroup,
    Checkbox,
    Radio,
    RadioGroup,
    FormControlLabel as RadioFormLabel,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Collapse,
    Skeleton,
    Badge,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    alpha,
    MenuList,
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Block as BlockIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    Event as EventIcon,
    Videocam as VideocamIcon,
    People as PeopleIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Schedule as ScheduleIcon,
    Save as SaveIcon,
    Visibility as VisibilityIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    FilterAltOff as FilterListIcon,
    Download as DownloadIcon,
    Settings as SettingsIcon,
    Security as SecurityIcon,
    CloudUpload as CloudUploadIcon,
    VideoSettings as VideoSettingsIcon,
    GraphicEq as MicIcon,
    VolumeUp as VolumeUpIcon,
    Slideshow as ScreenShareIcon,
    Chat as ChatIcon,
    People as RecordIcon,
    PlayCircleRounded as PlayIcon,
    PauseCircleRounded as PauseIcon,
    CancelRounded as StopIcon,
    SettingsApplications as SettingsApplicationsIcon,
    NetworkCheck as NetworkIcon,
    Storage as StorageIcon,
    Cloud as CloudIcon,
    VideoSettingsRounded as VideocamOffIcon,
    SupportAgent as HeadsetIcon,
    AutoAwesome as PaletteIcon,
    CheckCircle as CheckBoxIcon,
    CheckCircleOutlineOutlined as CheckBoxOutlineBlankIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Verified as VerifiedIcon,
    GraphicEq as AudioIcon,
    Restore as RestoreIcon,
    Timer as TimerIcon,
    Speed as SpeedIcon,
    FiberManualRecord as LiveIcon,
    CancelRounded as EndCallIcon,
    VolumeOff as MicOffIcon,
    VideoSettingsRounded as VideoOffIcon,
    Slideshow as ScreenIcon,
    Group as GroupIcon,
    Star as StarIcon,
    Star as StarBorderIcon,
    Warning as WarningIcon,
    Dashboard as DashboardIcon,
    Analytics as AnalyticsIcon,
    NetworkCheck as EthernetIcon,
    Assessment as AssessmentIcon,
    AdminPanelSettings as AdminIcon,
    AutoAwesome as AutoAwesomeIcon,
    Edit as BrushIcon,
    VerifiedUser as VerifiedUserIcon,
    Lock as LockIcon,
    Public as PublicIcon,
    CalendarToday as CalendarTodayIcon,
    Language as LanguageIcon,
    PaidOutlined as PaidIcon,
    Money as MoneyIcon,
    CreditCard as CreditCardIcon,
    Receipt as ReceiptIcon,
    Storefront as StorefrontIcon,
    Business as BusinessIcon,
    ArrowForward as ArrowForwardIcon,
    Assignment as AssignmentIcon,
    NotificationsActive as NotificationsIcon,
    WarningAmber as WarningAmberIcon,
    Restore as HistoryIcon,
    PersonAdd as PersonAddIcon,
    Send as SendIcon,
    Print as PrintIcon,
    RocketLaunch as RocketIcon,
    AutoAwesome as AutoAwesomeIcon2,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import {
    collection,
    getDocs,
    getDoc,
    doc,
    updateDoc,
    deleteDoc,
    setDoc,
    serverTimestamp,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
    addDoc,
    Timestamp,
    increment,
    arrayUnion,
    arrayRemove,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

// ─────────────────────────────────────────────────────────────────────────────
// 1 · DESIGN TOKENS — "TelcoFair Glass Workspace" (Enhanced)
// ─────────────────────────────────────────────────────────────────────────────

const palette = {
    canvas: '#F2F7F9',
    canvasAlt: '#F7FAFC',
    paper: 'rgba(255,255,255,0.88)',
    paperSolid: '#FFFFFF',
    paperAlt: '#F7FAFC',
    ink: '#182235',
    inkSoft: '#506175',
    inkLight: '#7B8B9C',
    inkMuted: '#AAB6C1',
    hairline: 'rgba(31,62,82,0.09)',
    hairlineStrong: 'rgba(31,62,82,0.14)',
    rust: 'var(--primary, #2E6F8E)',
    rustDeep: 'var(--secondary, #42B89B)',
    rustLight: '#5DA6C5',
    pine: '#2E8B69',
    pineSoft: '#4CA17F',
    pineLight: '#69B99A',
    gold: '#D78B28',
    goldLight: '#E2A54D',
    teal: '#268AA3',
    tealLight: '#5EADC0',
    purple: '#8D5AC8',
    purpleLight: '#A97AD9',
    navy: '#315E9E',
    navyLight: '#5E84BD',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#3B82F6',
    admin: '#7557C7',
    clay: '#B85E4B',
    coral: '#D96F68',
    sage: '#6FA58C',
    lavender: '#9878BF',
    honey: '#E5BC68',
    ocean: '#4A8B9B',
};

const rgba = (color, a = 1) => {
    if (typeof color === 'string' && color.startsWith('var(')) return `color-mix(in srgb, ${color} ${Math.max(0, Math.min(100, Number(a) * 100))}%, transparent)`;
    if (typeof color === 'string' && color.startsWith('rgb')) return color;
    const hex = color.replace('#', '');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontMono = "'JetBrains Mono', 'SFMono-Regular', monospace";

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');`;

// ─────────────────────────────────────────────────────────────────────────────
// 2 · LIQUID GLASS FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

const liquidGlass = (accent = palette.rust, intensity = 0.12, elevation = 'md') => {
    const elevations = {
        xs: `0 1px 2px ${rgba(palette.ink, 0.04)}, 0 1px 4px ${rgba(palette.ink, 0.04)}`,
        sm: `0 2px 4px ${rgba(palette.ink, 0.06)}, 0 4px 12px ${rgba(palette.ink, 0.08)}`,
        md: `0 4px 8px ${rgba(palette.ink, 0.08)}, 0 8px 20px ${rgba(palette.ink, 0.10)}`,
        lg: `0 8px 16px ${rgba(palette.ink, 0.10)}, 0 16px 40px ${rgba(palette.ink, 0.12)}`,
        xl: `0 16px 32px ${rgba(palette.ink, 0.12)}, 0 24px 64px ${rgba(palette.ink, 0.15)}`,
    };

    return {
        background: `linear-gradient(155deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.50) 42%, ${rgba(accent, intensity)} 100%)`,
        backdropFilter: 'blur(24px) saturate(200%)',
        WebkitBackdropFilter: 'blur(24px) saturate(200%)',
        border: `1px solid rgba(255,255,255,0.6)`,
        boxShadow: [
            `inset 0 1px 0 rgba(255,255,255,0.95)`,
            `inset 0 -1px 0 rgba(255,255,255,0.30)`,
            `inset 0 0 0 1px ${rgba(accent, 0.04)}`,
            elevations[elevation] || elevations.md,
            `0 12px 40px -8px ${rgba(accent, 0.15)}`,
        ].join(', '),
        overflow: 'hidden',
        transition: 'box-shadow .6s cubic-bezier(.22,1,.36,1), transform .6s cubic-bezier(.22,1,.36,1), border-color .5s ease',
    };
};

const sheenLayer = (accent) => ({
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 'var(--sheen, 0)',
    transition: 'opacity .5s ease',
    background: `radial-gradient(420px circle at var(--mx, 50%) var(--my, 0%), rgba(255,255,255,0.7), ${rgba(accent, 0.10)} 38%, transparent 68%)`,
    mixBlendMode: 'soft-light',
    content: '""',
    borderRadius: 'inherit',
});

const useSheen = () => {
    const ref = useRef(null);
    const onPointerMove = (e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
        el.style.setProperty('--sheen', '1');
    };
    const onPointerLeave = () => {
        const el = ref.current;
        if (el) el.style.setProperty('--sheen', '0');
    };
    return { ref, onPointerMove, onPointerLeave };
};

// ─────────────────────────────────────────────────────────────────────────────
// 3 · GLASS COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const GlassCard = ({ children, accent = palette.rust, sx = {}, delay = 0, hover = true, elevation = 'md', ...props }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
            whileHover={hover ? { y: -6, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } } : {}}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            style={{ height: '100%', width: '100%' }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquidGlass(accent, 0.12, elevation),
                    position: 'relative',
                    p: { xs: 2.5, md: 3 },
                    borderRadius: '20px',
                    border: `1.5px solid ${rgba(accent, 0.10)}`,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    '&::after': sheenLayer(accent),
                    transform: isHovered && hover ? 'translateY(-4px) scale(1.003)' : 'translateY(0) scale(1)',
                    boxShadow: isHovered && hover 
                        ? [
                            `inset 0 1px 0 rgba(255,255,255,0.95)`,
                            `inset 0 -1px 0 rgba(255,255,255,0.30)`,
                            `inset 0 0 0 1px ${rgba(accent, 0.08)}`,
                            `0 20px 60px -16px ${rgba(accent, 0.30)}`,
                            `0 8px 24px ${rgba(palette.ink, 0.10)}`,
                        ].join(', ')
                        : liquidGlass(accent, 0.12, elevation).boxShadow,
                    transition: 'all 0.4s cubic-bezier(.22,1,.36,1)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, transparent, ${rgba(accent, 0.4)}, transparent)`,
                        opacity: isHovered && hover ? 1 : 0.4,
                        transition: 'opacity 0.4s ease',
                        pointerEvents: 'none',
                        borderRadius: '20px 20px 0 0',
                        zIndex: 2,
                    },
                    ...sx,
                }}
                {...props}
            >
                {children}
            </Paper>
        </motion.div>
    );
};

const GlassMenuItem = ({ children, onClick, isActive, color = palette.rust, delay = 0, ...props }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ width: '100%' }}
        >
            <MenuItem
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                onClick={onClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={{
                    position: 'relative',
                    borderRadius: '12px',
                    marginX: '6px',
                    marginBottom: '2px',
                    padding: '10px 14px',
                    background: isActive ? rgba(color, 0.08) : 'transparent',
                    transition: 'all 0.3s cubic-bezier(.22,1,.36,1)',
                    transform: isHovered ? 'translateX(6px)' : 'translateX(0)',
                    backdropFilter: isHovered ? 'blur(4px)' : 'none',
                    '&:hover': {
                        background: rgba(color, 0.10),
                        transform: 'translateX(6px)',
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '10%',
                        height: '80%',
                        width: '3px',
                        borderRadius: '0 3px 3px 0',
                        background: isActive ? color : 'transparent',
                        transition: 'background 0.3s ease',
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        opacity: isHovered ? 1 : 0,
                        transition: 'opacity .4s ease',
                        background: `radial-gradient(300px circle at var(--mx, 50%) var(--my, 0%), rgba(255,255,255,0.5), ${rgba(color, 0.06)} 40%, transparent 70%)`,
                        mixBlendMode: 'soft-light',
                        borderRadius: '12px',
                    },
                    ...props.sx,
                }}
            >
                {children}
            </MenuItem>
        </motion.div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// 4 · FLOATING BACKGROUND ORBS
// ─────────────────────────────────────────────────────────────────────────────

const FloatingOrbs = () => {
    const orbs = [
        { color: palette.rust, size: 400, top: '-10%', left: '2%', duration: 28, delay: 0 },
        { color: palette.pine, size: 340, top: '15%', left: '85%', duration: 34, delay: 2 },
        { color: palette.gold, size: 280, top: '55%', left: '-8%', duration: 26, delay: 1 },
        { color: palette.purple, size: 320, top: '75%', left: '72%', duration: 32, delay: 3 },
        { color: palette.teal, size: 240, top: '35%', left: '45%', duration: 30, delay: 1.5 },
    ];

    return (
        <Box aria-hidden="true" sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
            {orbs.map((orb, i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0.25, 0.5, 0.25], x: [0, 40, -30, 0], y: [0, -35, 25, 0], scale: [1, 1.1, 0.95, 1] }}
                    transition={{ duration: orb.duration, delay: orb.delay, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute',
                        top: orb.top,
                        left: orb.left,
                        width: orb.size,
                        height: orb.size,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${orb.color}20, ${orb.color}00 70%)`,
                        filter: 'blur(8px)',
                    }}
                />
            ))}
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// 5 · SECTION RULE
// ─────────────────────────────────────────────────────────────────────────────

const SectionRule = ({ color = palette.rust }) => (
    <Box sx={{ width: 40, height: 3, borderRadius: 2, background: `linear-gradient(90deg, ${color}, transparent)`, mt: 0.75 }} />
);

// ─────────────────────────────────────────────────────────────────────────────
// 6 · TAB PANEL
// ─────────────────────────────────────────────────────────────────────────────

function TabPanel({ children, value, index, ...props }) {
    return (
        <div role="tabpanel" hidden={value !== index} {...props}>
            {value === index && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>
                </motion.div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// 7 · STATUS CHIPS
// ─────────────────────────────────────────────────────────────────────────────

const SponsorshipStatusChip = ({ status }) => {
    const statusConfig = {
        active: { label: 'Active', color: palette.pine, icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
        pending: { label: 'Pending', color: palette.gold, icon: <ScheduleIcon sx={{ fontSize: 14 }} /> },
        expired: { label: 'Expired', color: palette.clay, icon: <CancelIcon sx={{ fontSize: 14 }} /> },
        cancelled: { label: 'Cancelled', color: palette.inkSoft, icon: <BlockIcon sx={{ fontSize: 14 }} /> },
        completed: { label: 'Completed', color: palette.teal, icon: <CheckIcon sx={{ fontSize: 14 }} /> },
        about_to_expire: { label: 'Expiring Soon', color: palette.gold, icon: <WarningAmberIcon sx={{ fontSize: 14 }} /> }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
        <Chip
            icon={config.icon}
            label={config.label}
            size="small"
            sx={{
                bgcolor: `${config.color}15`,
                color: config.color,
                fontWeight: 600,
                height: 24,
                fontFamily: fontBody,
                backdropFilter: 'blur(4px)',
                '& .MuiChip-icon': { color: config.color }
            }}
        />
    );
};

const PaymentStatusChip = ({ status }) => {
    const statusConfig = {
        paid: { label: 'Paid', color: palette.pine, icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
        pending: { label: 'Pending', color: palette.gold, icon: <ScheduleIcon sx={{ fontSize: 14 }} /> },
        failed: { label: 'Failed', color: palette.clay, icon: <CancelIcon sx={{ fontSize: 14 }} /> },
        refunded: { label: 'Refunded', color: palette.teal, icon: <RestoreIcon sx={{ fontSize: 14 }} /> },
        partial: { label: 'Partial', color: palette.purple, icon: <MoneyIcon sx={{ fontSize: 14 }} /> },
        manual: { label: 'Manual', color: palette.rust, icon: <AssignmentIcon sx={{ fontSize: 14 }} /> }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
        <Chip
            icon={config.icon}
            label={config.label}
            size="small"
            sx={{
                bgcolor: `${config.color}15`,
                color: config.color,
                fontWeight: 600,
                height: 24,
                fontFamily: fontBody,
                backdropFilter: 'blur(4px)',
                '& .MuiChip-icon': { color: config.color }
            }}
        />
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// 8 · STAT CARD (Enhanced Glass)
// ─────────────────────────────────────────────────────────────────────────────

const StatCard = ({ title, value, icon, color, gradient, delay, subtitle, change, trend, loading }) => {
    const nodeRef = useRef(null);
    const hasRun = useRef(false);
    const [isHovered, setIsHovered] = useState(false);
    const { ref, onPointerMove, onPointerLeave } = useSheen();

    useEffect(() => {
        if (typeof value === 'number' && value > 0 && !loading) {
            const node = nodeRef.current;
            if (!node || hasRun.current) return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting && !hasRun.current) {
                        hasRun.current = true;
                        let current = 0;
                        const step = Math.ceil(value / 40);
                        const interval = setInterval(() => {
                            current += step;
                            if (current >= value) {
                                current = value;
                                clearInterval(interval);
                            }
                            if (node) node.textContent = Math.round(current).toLocaleString();
                        }, 25);
                        return () => clearInterval(interval);
                    }
                },
                { threshold: 0.3 }
            );

            observer.observe(node);
            return () => observer.disconnect();
        }
    }, [value, loading]);

    if (loading) {
        return (
            <GlassCard accent={color} hover={false}>
                <Skeleton variant="text" width="60%" height={20} sx={{ bgcolor: 'rgba(26,21,18,0.06)' }} />
                <Skeleton variant="text" width="40%" height={40} sx={{ mt: 1, bgcolor: 'rgba(26,21,18,0.06)' }} />
                <Skeleton variant="text" width="30%" height={16} sx={{ mt: 1, bgcolor: 'rgba(26,21,18,0.06)' }} />
            </GlassCard>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -6, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } }}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            style={{ height: '100%' }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquidGlass(color, 0.15, 'lg'),
                    position: 'relative',
                    borderRadius: '24px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&::after': sheenLayer(color),
                    transform: isHovered ? 'translateY(-6px) scale(1.005)' : 'translateY(0) scale(1)',
                    boxShadow: isHovered 
                        ? [
                            `inset 0 1px 0 rgba(255,255,255,0.95)`,
                            `inset 0 -1px 0 rgba(255,255,255,0.30)`,
                            `inset 0 0 0 1px ${rgba(color, 0.10)}`,
                            `0 24px 64px -16px ${rgba(color, 0.35)}`,
                            `0 12px 32px ${rgba(palette.ink, 0.12)}`,
                        ].join(', ')
                        : liquidGlass(color, 0.15, 'lg').boxShadow,
                    transition: 'all 0.4s cubic-bezier(.22,1,.36,1)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, transparent, ${rgba(color, 0.5)}, transparent)`,
                        opacity: isHovered ? 1 : 0.4,
                        transition: 'opacity 0.4s ease',
                        pointerEvents: 'none',
                        borderRadius: '24px 24px 0 0',
                        zIndex: 2,
                    },
                }}
            >
                <CardContent sx={{ p: { xs: 2.5, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <motion.div
                            whileHover={{ scale: 1.12, rotate: -5, transition: { type: 'spring', stiffness: 400, damping: 17 } }}
                            style={{
                                width: 56,
                                height: 56,
                                borderRadius: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: gradient || `linear-gradient(135deg, ${color}, ${color}80)`,
                                color: '#fff',
                                boxShadow: `0 4px 20px ${rgba(color, 0.35)}`,
                                position: 'relative',
                                border: `2px solid ${rgba(color, 0.15)}`,
                                willChange: 'transform',
                                backdropFilter: 'blur(4px)',
                            }}
                        >
                            {React.cloneElement(icon, { sx: { fontSize: 24 } })}
                        </motion.div>

                        {change && (
                            <Chip
                                label={change}
                                size="small"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    height: 26,
                                    px: 0.75,
                                    bgcolor: trend === 'up' ? rgba(palette.pine, 0.12) : rgba(palette.danger, 0.12),
                                    color: trend === 'up' ? palette.pine : palette.danger,
                                    border: `1.5px solid ${trend === 'up' ? rgba(palette.pine, 0.2) : rgba(palette.danger, 0.2)}`,
                                    backdropFilter: 'blur(4px)',
                                }}
                                icon={trend === 'up' ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
                            />
                        )}
                    </Box>

                    <Typography
                        sx={{
                            fontFamily: fontDisplay,
                            fontWeight: 700,
                            mb: 0.5,
                            fontSize: { xs: '2rem', md: '2.5rem' },
                            lineHeight: 1,
                            background: `linear-gradient(135deg, ${palette.ink} 30%, ${color})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            display: 'inline-block',
                        }}
                    >
                        {typeof value === 'number' ? (
                            <span ref={nodeRef}>0</span>
                        ) : (
                            value
                        )}
                    </Typography>

                    <Typography sx={{
                        color: palette.inkSoft,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        fontSize: '0.7rem'
                    }}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography sx={{ color: palette.inkLight, fontSize: '0.7rem', mt: 0.25 }}>
                            {subtitle}
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto', pt: 2 }}>
                        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: palette.success, position: 'relative', zIndex: 1 }} />
                            <motion.div
                                animate={{
                                    scale: [1, 1.5, 1],
                                    opacity: [0.3, 0.1, 0.3],
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: 'easeInOut',
                                }}
                                style={{
                                    position: 'absolute',
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    background: palette.success,
                                    opacity: 0.3,
                                }}
                            />
                        </Box>
                        <Typography variant="caption" sx={{ color: palette.success, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem' }}>
                            Live Data
                        </Typography>
                    </Box>
                </CardContent>
            </Paper>
        </motion.div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// 9 · MAIN SPONSORSHIPS COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const Sponsorships = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    
    // State
    const [loading, setLoading] = useState(true);
    const [pageLoading, setPageLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [sponsorships, setSponsorships] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [users, setUsers] = useState([]);
    const [packages, setPackages] = useState([]);
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        pending: 0,
        revenue: 0,
        expiring: 0
    });
    const [searchQuery, setSearchQuery] = useState('');
    
    // Dialog States
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState('add');
    const [selectedSponsorship, setSelectedSponsorship] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
    const [openManualAssign, setOpenManualAssign] = useState(false);
    const [manualAssignData, setManualAssignData] = useState({
        userId: '',
        packageId: '',
        startDate: '',
        endDate: '',
        amount: 0,
        paymentStatus: 'manual',
        notes: ''
    });
    const [openPaymentDetails, setOpenPaymentDetails] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    
    // Form Data
    const [formData, setFormData] = useState({
        companyId: '',
        packageType: 'premium',
        amount: 500,
        currency: 'USD',
        paymentStatus: 'pending',
        startDate: '',
        endDate: '',
        features: [],
        displayOnHome: true,
        displayOnApp: true,
        priority: 1,
        autoRenew: false,
        paymentMethod: 'card',
        transactionId: '',
        notes: ''
    });

    // Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Package options
    const packageOptions = [
        { value: 'premium', label: 'Premium', amount: 500, duration: 30, color: palette.rust, features: ['Featured Listing', 'Basic Analytics', 'Email Support'], displayDuration: '30 days' },
        { value: 'gold', label: 'Gold', amount: 1000, duration: 60, color: palette.gold, features: ['Priority Listing', 'Advanced Analytics', 'Priority Support', 'Logo Placement'], displayDuration: '60 days' },
        { value: 'platinum', label: 'Platinum', amount: 2000, duration: 90, color: '#E5E4E2', features: ['Top Listing', 'Premium Analytics', '24/7 Support', 'Logo Placement', 'Sponsored Content'], displayDuration: '90 days' },
        { value: 'diamond', label: 'Diamond', amount: 5000, duration: 180, color: '#B9F2FF', features: ['Exclusive Listing', 'Custom Analytics', 'Dedicated Support', 'Premium Placement', 'Sponsored Content', 'VIP Access'], displayDuration: '180 days' }
    ];

    // Default packages for manual assignment
    const defaultPackages = [
        { id: 'premium_pkg', name: 'Premium Package', price: 500, duration: 30, description: 'Basic sponsorship package' },
        { id: 'gold_pkg', name: 'Gold Package', price: 1000, duration: 60, description: 'Advanced sponsorship package' },
        { id: 'platinum_pkg', name: 'Platinum Package', price: 2000, duration: 90, description: 'Premium sponsorship package' },
        { id: 'diamond_pkg', name: 'Diamond Package', price: 5000, duration: 180, description: 'Elite sponsorship package' }
    ];

    // Canvas styles
    const canvasSx = {
        minHeight: '100vh',
        position: 'relative',
        bgcolor: palette.canvas,
        fontFamily: fontBody,
        '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.3,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.025%27/%3E%3C/svg%3E")',
            animation: 'grainDrift 9s steps(6) infinite',
        },
    };

    // ─────────────────────────────────────────────────────────────────────────
    // FETCH FUNCTIONS
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        const loadData = async () => {
            setPageLoading(true);
            await Promise.all([
                fetchSponsorships(),
                fetchCompanies(),
                fetchUsers(),
                fetchPackages()
            ]);
            setPageLoading(false);
        };
        loadData();
        
        const unsubscribe = onSnapshot(
            query(collection(db, 'sponsorships'), orderBy('createdAt', 'desc')),
            (snapshot) => {
                const sponsorshipsList = [];
                snapshot.forEach((doc) => {
                    sponsorshipsList.push({ id: doc.id, ...doc.data() });
                });
                setSponsorships(sponsorshipsList);
                calculateStats(sponsorshipsList);
            },
            (error) => {
                console.warn('Sponsorships listener error:', error);
            }
        );

        return () => unsubscribe();
    }, []);

    const fetchSponsorships = async () => {
        setLoading(true);
        try {
            const snapshot = await getDocs(collection(db, 'sponsorships'));
            const sponsorshipsList = [];
            snapshot.forEach((doc) => {
                sponsorshipsList.push({ id: doc.id, ...doc.data() });
            });
            setSponsorships(sponsorshipsList);
            calculateStats(sponsorshipsList);
        } catch (error) {
            console.error('Error fetching sponsorships:', error);
            toast.error('Failed to load sponsorships');
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanies = async () => {
        try {
            const snapshot = await getDocs(collection(db, 'companies'));
            const companiesList = [];
            snapshot.forEach((doc) => {
                companiesList.push({ id: doc.id, ...doc.data() });
            });
            setCompanies(companiesList);
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const snapshot = await getDocs(collection(db, 'users'));
            const usersList = [];
            snapshot.forEach((doc) => {
                usersList.push({ id: doc.id, ...doc.data() });
            });
            setUsers(usersList);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchPackages = async () => {
        try {
            let packagesList = [];
            
            try {
                const snapshot = await getDocs(collection(db, 'packages'));
                snapshot.forEach((doc) => {
                    packagesList.push({ id: doc.id, ...doc.data() });
                });
            } catch (error) {
                console.log('No packages found in "packages" collection');
            }
            
            if (packagesList.length === 0) {
                try {
                    const snapshot = await getDocs(collection(db, 'sponsorshipPackages'));
                    snapshot.forEach((doc) => {
                        packagesList.push({ id: doc.id, ...doc.data() });
                    });
                } catch (error) {
                    console.log('No packages found in "sponsorshipPackages" collection');
                }
            }
            
            if (packagesList.length === 0) {
                packagesList = defaultPackages;
            }
            
            setPackages(packagesList);
        } catch (error) {
            console.error('Error fetching packages:', error);
            setPackages(defaultPackages);
        }
    };

    const calculateStats = (sponsorshipsList) => {
        const total = sponsorshipsList.length;
        const active = sponsorshipsList.filter(s => s.isActive === true).length;
        const pending = sponsorshipsList.filter(s => s.paymentStatus === 'pending').length;
        const revenue = sponsorshipsList.reduce((acc, s) => {
            if (s.paymentStatus === 'paid' || s.paymentStatus === 'manual') {
                return acc + (s.amount || 0);
            }
            return acc;
        }, 0);
        
        const now = new Date();
        const expiring = sponsorshipsList.filter(s => {
            if (!s.isActive || !s.endDate) return false;
            const endDate = new Date(s.endDate);
            const diffDays = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
            return diffDays > 0 && diffDays <= 7;
        }).length;

        setStats({ total, active, pending, revenue, expiring });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // HANDLERS
    // ─────────────────────────────────────────────────────────────────────────

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleOpenAddDialog = () => {
        setDialogMode('add');
        setSelectedSponsorship(null);
        setFormData({
            companyId: '',
            packageType: 'premium',
            amount: 500,
            currency: 'USD',
            paymentStatus: 'pending',
            startDate: '',
            endDate: '',
            features: packageOptions.find(p => p.value === 'premium')?.features || [],
            displayOnHome: true,
            displayOnApp: true,
            priority: 1,
            autoRenew: false,
            paymentMethod: 'card',
            transactionId: '',
            notes: ''
        });
        setOpenDialog(true);
    };

    const handleOpenEditDialog = async (sponsorship) => {
        setDialogMode('edit');
        setSelectedSponsorship(sponsorship);
        setFormData({
            companyId: sponsorship.companyId || '',
            packageType: sponsorship.packageType || 'premium',
            amount: sponsorship.amount || 500,
            currency: sponsorship.currency || 'USD',
            paymentStatus: sponsorship.paymentStatus || 'pending',
            startDate: sponsorship.startDate || '',
            endDate: sponsorship.endDate || '',
            features: sponsorship.features || [],
            displayOnHome: sponsorship.displayOnHome !== undefined ? sponsorship.displayOnHome : true,
            displayOnApp: sponsorship.displayOnApp !== undefined ? sponsorship.displayOnApp : true,
            priority: sponsorship.priority || 1,
            autoRenew: sponsorship.autoRenew || false,
            paymentMethod: sponsorship.paymentMethod || 'card',
            transactionId: sponsorship.transactionId || '',
            notes: sponsorship.notes || ''
        });
        setOpenDialog(true);
    };

    const handlePackageChange = (packageType) => {
        const pkg = packageOptions.find(p => p.value === packageType);
        setFormData({
            ...formData,
            packageType,
            amount: pkg?.amount || 500,
            features: pkg?.features || []
        });
    };

    const handleSaveSponsorship = async () => {
        try {
            if (!formData.companyId || !formData.startDate || !formData.endDate) {
                toast.error('Please fill all required fields');
                return;
            }

            const isActive = formData.paymentStatus === 'paid' || formData.paymentStatus === 'manual';
            const timestamp = serverTimestamp();
            
            const sponsorshipData = {
                companyId: formData.companyId,
                packageType: formData.packageType,
                amount: formData.amount,
                currency: formData.currency,
                paymentStatus: formData.paymentStatus,
                startDate: formData.startDate,
                endDate: formData.endDate,
                features: formData.features,
                isActive: isActive,
                displayOnHome: formData.displayOnHome,
                displayOnApp: formData.displayOnApp,
                priority: formData.priority,
                autoRenew: formData.autoRenew,
                paymentMethod: formData.paymentMethod,
                transactionId: formData.transactionId || '',
                notes: formData.notes || '',
                updatedAt: timestamp,
                updatedBy: user?.uid
            };

            if (dialogMode === 'add') {
                sponsorshipData.sponsorshipId = `sponsorship_${Date.now()}`;
                sponsorshipData.createdAt = timestamp;
                sponsorshipData.createdBy = user?.uid;
                await setDoc(doc(db, 'sponsorships', sponsorshipData.sponsorshipId), sponsorshipData);
                toast.success('Sponsorship created successfully!');
            } else {
                await updateDoc(doc(db, 'sponsorships', selectedSponsorship.id), sponsorshipData);
                toast.success('Sponsorship updated successfully!');
            }

            setOpenDialog(false);
            fetchSponsorships();
        } catch (error) {
            console.error('Error saving sponsorship:', error);
            toast.error('Failed to save sponsorship');
        }
    };

    const handleDeleteSponsorship = async () => {
        try {
            await deleteDoc(doc(db, 'sponsorships', deleteConfirm.id));
            toast.success('Sponsorship deleted successfully!');
            setDeleteConfirm({ open: false, id: null });
            fetchSponsorships();
        } catch (error) {
            console.error('Error deleting sponsorship:', error);
            toast.error('Failed to delete sponsorship');
        }
    };

    const handleToggleActive = async (sponsorshipId, currentStatus) => {
        try {
            await updateDoc(doc(db, 'sponsorships', sponsorshipId), {
                isActive: !currentStatus,
                updatedAt: serverTimestamp()
            });
            toast.success(`Sponsorship ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
            fetchSponsorships();
        } catch (error) {
            console.error('Error toggling sponsorship:', error);
            toast.error('Failed to update sponsorship');
        }
    };

    const handleManualAssign = async () => {
        try {
            if (!manualAssignData.userId || !manualAssignData.packageId || !manualAssignData.startDate || !manualAssignData.endDate) {
                toast.error('Please fill all required fields');
                return;
            }

            const selectedPackage = packages.find(p => p.id === manualAssignData.packageId);
            const userData = users.find(u => u.id === manualAssignData.userId);
            const timestamp = serverTimestamp();
            
            const sponsorshipData = {
                userId: manualAssignData.userId,
                userName: userData?.displayName || 'Unknown User',
                userEmail: userData?.email || '',
                packageId: manualAssignData.packageId,
                packageName: selectedPackage?.name || 'Custom Package',
                amount: manualAssignData.amount || selectedPackage?.price || 0,
                currency: 'USD',
                paymentStatus: 'manual',
                startDate: manualAssignData.startDate,
                endDate: manualAssignData.endDate,
                isActive: true,
                displayOnHome: true,
                displayOnApp: true,
                priority: 1,
                autoRenew: false,
                paymentMethod: 'manual',
                notes: manualAssignData.notes || 'Manually assigned',
                assignedBy: user?.uid,
                assignedByEmail: user?.email,
                sponsorshipId: `manual_${Date.now()}`,
                createdAt: timestamp,
                updatedAt: timestamp
            };

            await setDoc(doc(db, 'sponsorships', sponsorshipData.sponsorshipId), sponsorshipData);
            toast.success('Sponsorship assigned manually successfully!');
            setOpenManualAssign(false);
            setManualAssignData({
                userId: '',
                packageId: '',
                startDate: '',
                endDate: '',
                amount: 0,
                paymentStatus: 'manual',
                notes: ''
            });
            fetchSponsorships();
        } catch (error) {
            console.error('Error assigning sponsorship:', error);
            toast.error('Failed to assign sponsorship');
        }
    };

    const handleViewPaymentDetails = (sponsorship) => {
        setSelectedPayment(sponsorship);
        setOpenPaymentDetails(true);
    };

    const handleViewDetails = (sponsorshipId) => {
        navigate(`/sponsorships/${sponsorshipId}`);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getCompanyName = (companyId) => {
        const company = companies.find(c => c.id === companyId);
        return company?.companyName || 'Unknown Company';
    };

    const getDaysRemaining = (endDate) => {
        if (!endDate) return 'N/A';
        const now = new Date();
        const end = new Date(endDate);
        const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) return 'Expired';
        if (diffDays === 0) return 'Today';
        return `${diffDays} days`;
    };

    const filteredSponsorships = () => {
        const now = new Date();
        let filtered = sponsorships;
        
        // Apply search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(s => 
                (s.userName?.toLowerCase().includes(query)) ||
                (s.userEmail?.toLowerCase().includes(query)) ||
                (s.packageType?.toLowerCase().includes(query)) ||
                (s.sponsorshipId?.toLowerCase().includes(query))
            );
        }
        
        // Apply tab filter
        if (tabValue === 1) {
            filtered = filtered.filter(s => s.isActive === true);
        } else if (tabValue === 2) {
            filtered = filtered.filter(s => s.paymentStatus === 'pending');
        } else if (tabValue === 3) {
            filtered = filtered.filter(s => s.isActive === false && s.paymentStatus !== 'pending');
        } else if (tabValue === 4) {
            filtered = filtered.filter(s => {
                if (!s.isActive || !s.endDate) return false;
                const endDate = new Date(s.endDate);
                const diffDays = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                return diffDays > 0 && diffDays <= 7;
            });
        }
        
        return filtered;
    };

    const statsData = [
        {
            title: 'Total Sponsorships',
            value: stats.total,
            icon: <StorefrontIcon  key="config-1267-0-2" />,
            color: palette.rust,
            gradient: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
            subtitle: 'All packages',
            change: '+12%',
            trend: 'up',
            loading,
        },
        {
            title: 'Active',
            value: stats.active,
            icon: <CheckCircleIcon  key="config-1278-1-2" />,
            color: palette.pine,
            gradient: `linear-gradient(135deg, ${palette.pine}, ${palette.pineSoft})`,
            subtitle: 'Active sponsorships',
            change: '+8%',
            trend: 'up',
            loading,
        },
        {
            title: 'Pending',
            value: stats.pending,
            icon: <ScheduleIcon  key="config-1289-2-2" />,
            color: palette.gold,
            gradient: `linear-gradient(135deg, ${palette.gold}, ${palette.goldLight})`,
            subtitle: 'Awaiting payment',
            change: '+5%',
            trend: 'up',
            loading,
        },
        {
            title: 'Revenue',
            value: `$${stats.revenue}`,
            icon: <MoneyIcon  key="config-1300-3-2" />,
            color: palette.teal,
            gradient: `linear-gradient(135deg, ${palette.teal}, ${palette.tealLight})`,
            subtitle: 'Total collected',
            change: '+15%',
            trend: 'up',
            loading,
        },
        {
            title: 'Expiring Soon',
            value: stats.expiring,
            icon: <WarningAmberIcon  key="config-1311-4-2" />,
            color: palette.gold,
            gradient: `linear-gradient(135deg, ${palette.gold}, ${palette.goldLight})`,
            subtitle: 'Within 7 days',
            change: '+3%',
            trend: 'up',
            loading,
        },
    ];

    const tabs = [
        { label: 'All Sponsorships', icon: <StorefrontIcon  key="config-1325-0-1" />, color: palette.rust },
        { label: 'Active', icon: <CheckCircleIcon  key="config-1326-1-1" />, color: palette.pine },
        { label: 'Pending', icon: <ScheduleIcon  key="config-1327-2-1" />, color: palette.gold },
        { label: 'Expired', icon: <CancelIcon  key="config-1328-3-1" />, color: palette.clay },
        { label: 'Expiring Soon', icon: <WarningAmberIcon  key="config-1329-4-1" />, color: palette.gold }
    ];

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER FUNCTIONS
    // ─────────────────────────────────────────────────────────────────────────

    const renderSponsorshipTable = () => {
        const filtered = filteredSponsorships();
        
        if (filtered.length === 0) {
            return (
                <GlassCard accent={palette.inkLight} hover={false} elevation="md">
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <StorefrontIcon sx={{ fontSize: 56, color: palette.inkLight, opacity: 0.3, mb: 2 }} />
                        <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                            No Sponsorships Found
                        </Typography>
                        <Typography variant="body2" sx={{ color: palette.inkSoft, mt: 0.5, mb: 3 }}>
                            {tabValue === 0 ? 'Add your first sponsorship package or manually assign one to a user' :
                             tabValue === 1 ? 'No active sponsorships found' :
                             tabValue === 2 ? 'No pending sponsorships found' :
                             tabValue === 3 ? 'No expired sponsorships found' :
                             'No sponsorships expiring soon'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                                <Button
                                    variant="contained"
                                    disableElevation
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenAddDialog}
                                    sx={{
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 3,
                                        py: 1.1,
                                        background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                        boxShadow: `0 6px 16px -8px rgba(199,91,46,0.45)`,
                                        '&:hover': {
                                            boxShadow: `0 8px 24px -8px rgba(199,91,46,0.55)`,
                                            transform: 'translateY(-2px)',
                                        }
                                    }}
                                >
                                    Create Sponsorship
                                </Button>
                            </motion.div>
                            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                                <Button
                                    variant="outlined"
                                    disableElevation
                                    startIcon={<PersonAddIcon />}
                                    onClick={() => setOpenManualAssign(true)}
                                    sx={{
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: palette.teal,
                                        color: palette.teal,
                                        '&:hover': {
                                            borderColor: palette.teal,
                                            bgcolor: `${palette.teal}08`,
                                        }
                                    }}
                                >
                                    Manual Assign
                                </Button>
                            </motion.div>
                        </Box>
                    </Box>
                </GlassCard>
            );
        }

        return (
            <GlassCard accent={palette.teal} hover={false} elevation="md">
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: palette.canvasAlt }}>
                                <TableCell sx={{ fontWeight: 700, color: palette.ink, fontFamily: fontBody }}>Company / User</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: palette.ink, fontFamily: fontBody }}>Package</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: palette.ink, fontFamily: fontBody }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: palette.ink, fontFamily: fontBody }}>Period</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: palette.ink, fontFamily: fontBody }}>Days Left</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: palette.ink, fontFamily: fontBody }}>Payment</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: palette.ink, fontFamily: fontBody }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: palette.ink, fontFamily: fontBody }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map((sponsorship, index) => {
                                const packageColor = packageOptions.find(p => p.value === sponsorship.packageType)?.color || palette.rust;
                                const daysLeft = getDaysRemaining(sponsorship.endDate);
                                const isExpiring = !isNaN(parseInt(daysLeft)) && parseInt(daysLeft) <= 7 && parseInt(daysLeft) > 0;
                                
                                return (
                                    <motion.tr
                                        key={sponsorship.id}
                                        initial={{ opacity: 0, y: 16 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: index * 0.05 }}
                                        style={{
                                            backgroundColor: isExpiring && sponsorship.isActive ? `${palette.gold}08` : 'transparent',
                                        }}
                                    >
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    sx={{
                                                        bgcolor: `${palette.rust}18`,
                                                        color: palette.rust,
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: '10px',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {sponsorship.userName?.charAt(0) || getCompanyName(sponsorship.companyId).charAt(0)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600} sx={{ color: palette.ink }}>
                                                        {sponsorship.userName || getCompanyName(sponsorship.companyId)}
                                                    </Typography>
                                                    {sponsorship.userEmail && (
                                                        <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                                            {sponsorship.userEmail}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={sponsorship.packageType || 'Premium'}
                                                size="small"
                                                sx={{
                                                    textTransform: 'capitalize',
                                                    fontWeight: 700,
                                                    bgcolor: `${packageColor}25`,
                                                    color: packageColor,
                                                    fontFamily: fontBody,
                                                    backdropFilter: 'blur(4px)',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontWeight: 700, color: palette.ink }}>
                                                ${sponsorship.amount || 0}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                                {sponsorship.currency || 'USD'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: palette.ink }}>
                                                {formatDate(sponsorship.startDate)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                                to {formatDate(sponsorship.endDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={daysLeft}
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    bgcolor: isExpiring ? `${palette.gold}15` : 'transparent',
                                                    color: isExpiring ? palette.gold : palette.inkSoft,
                                                    border: isExpiring ? `1px solid ${palette.gold}30` : 'none',
                                                    backdropFilter: 'blur(4px)',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <PaymentStatusChip status={sponsorship.paymentStatus} />
                                        </TableCell>
                                        <TableCell>
                                            <SponsorshipStatusChip status={sponsorship.isActive ? (isExpiring ? 'about_to_expire' : 'active') : 'expired'} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewDetails(sponsorship.id)}
                                                        sx={{ color: palette.teal, '&:hover': { bgcolor: `${palette.teal}12` } }}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Payment Details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewPaymentDetails(sponsorship)}
                                                        sx={{ color: palette.gold, '&:hover': { bgcolor: `${palette.gold}12` } }}
                                                    >
                                                        <ReceiptIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={sponsorship.isActive ? 'Deactivate' : 'Activate'}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleToggleActive(sponsorship.id, sponsorship.isActive)}
                                                        sx={{
                                                            color: sponsorship.isActive ? palette.pine : palette.inkSoft,
                                                            '&:hover': { bgcolor: `${sponsorship.isActive ? palette.pine : palette.inkSoft}12` }
                                                        }}
                                                    >
                                                        {sponsorship.isActive ? <CheckCircleIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenEditDialog(sponsorship)}
                                                        sx={{ color: palette.gold, '&:hover': { bgcolor: `${palette.gold}12` } }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setDeleteConfirm({ open: true, id: sponsorship.id })}
                                                        sx={{ color: palette.clay, '&:hover': { bgcolor: `${palette.clay}12` } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </motion.tr>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </GlassCard>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────

    if (pageLoading) {
        return (
            <Box sx={canvasSx}>
                <style>{FONT_IMPORT}</style>
                
                <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 4, lg: 6 }, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                        <motion.div
                            animate={{ 
                                rotate: 360,
                                scale: [1, 1.1, 1],
                            }}
                            transition={{ 
                                rotate: { duration: 2, repeat: Infinity, ease: 'linear' },
                                scale: { duration: 1.5, repeat: Infinity, ease: 'easeInOut' }
                            }}
                        >
                            <CircularProgress size={60} sx={{ color: palette.rust }} />
                        </motion.div>
                    </Box>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={canvasSx}>
            <style>{FONT_IMPORT}</style>
            <FloatingOrbs />
            

            <Container maxWidth={false} disableGutters sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, sm: 3, md: 4, lg: 6 }, position: 'relative', zIndex: 1, width: '100%' }}>
                
                {/* ────────────────────────────────────────────────────────────
                    HEADER
                    ──────────────────────────────────────────────────────────── */}
                <Box sx={{ mb: 4 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2.5 }}>
                            <Box>
                                <Typography
                                    sx={{
                                        fontFamily: fontDisplay,
                                        fontWeight: 600,
                                        fontSize: { xs: '1.6rem', md: '2rem' },
                                        color: palette.ink,
                                        letterSpacing: '-0.01em',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Sponsorships & Subscriptions
                                </Typography>
                                <Typography sx={{ color: palette.inkSoft, mt: 0.5, fontSize: '0.95rem' }}>
                                    Manage sponsorships, subscriptions and advertising packages
                                </Typography>
                                <SectionRule />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                {stats.expiring > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <Chip
                                            icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                                            label={`${stats.expiring} Expiring Soon`}
                                            sx={{
                                                bgcolor: `${palette.gold}15`,
                                                color: palette.gold,
                                                fontWeight: 700,
                                                letterSpacing: '0.04em',
                                                fontSize: '0.7rem',
                                                borderRadius: '20px',
                                                border: `1px solid ${palette.gold}30`,
                                                animation: 'pulse 2s infinite',
                                                backdropFilter: 'blur(4px)',
                                            }}
                                        />
                                    </motion.div>
                                )}
                                <Chip
                                    icon={<LiveIcon sx={{ fontSize: 11, color: `${palette.pine} !important` }} />}
                                    label={`${stats.active} Active`}
                                    sx={{
                                        bgcolor: 'rgba(47,89,66,0.10)',
                                        color: palette.pine,
                                        fontWeight: 700,
                                        letterSpacing: '0.04em',
                                        fontSize: '0.7rem',
                                        borderRadius: '20px',
                                        border: '1px solid rgba(47,89,66,0.18)',
                                        backdropFilter: 'blur(4px)',
                                    }}
                                />
                                <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={<AddIcon />}
                                        onClick={handleOpenAddDialog}
                                        sx={{
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            px: 2.75,
                                            py: 1.1,
                                            background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                            boxShadow: `0 6px 16px -8px rgba(199,91,46,0.45)`,
                                            '&:hover': {
                                                boxShadow: `0 8px 24px -8px rgba(199,91,46,0.55)`,
                                                transform: 'translateY(-2px)',
                                            }
                                        }}
                                    >
                                        Add Sponsorship
                                    </Button>
                                </motion.div>
                                <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                                    <Button
                                        variant="outlined"
                                        disableElevation
                                        startIcon={<PersonAddIcon />}
                                        onClick={() => setOpenManualAssign(true)}
                                        sx={{
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderColor: palette.teal,
                                            color: palette.teal,
                                            backdropFilter: 'blur(4px)',
                                            '&:hover': {
                                                borderColor: palette.teal,
                                                bgcolor: `${palette.teal}08`,
                                                transform: 'translateY(-2px)',
                                            }
                                        }}
                                    >
                                        Manual Assign
                                    </Button>
                                </motion.div>
                            </Box>
                        </Box>
                        <Divider sx={{ mt: 3, borderColor: palette.hairline }} />
                    </motion.div>
                </Box>

                {/* ────────────────────────────────────────────────────────────
                    STATS CARDS
                    ──────────────────────────────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr', md: '1fr 1fr 1fr', lg: 'repeat(5, 1fr)' },
                        gap: 2.5,
                        width: '100%',
                        mb: 4,
                    }}
                >
                    {statsData.map((stat, index) => (
                        <StatCard
                            key={stat.title}
                            title={stat.title}
                            value={stat.value}
                            icon={stat.icon}
                            color={stat.color}
                            gradient={stat.gradient}
                            delay={index * 0.05}
                            subtitle={stat.subtitle}
                            change={stat.change}
                            trend={stat.trend}
                            loading={stat.loading}
                        />
                    ))}
                </Box>

                {/* ────────────────────────────────────────────────────────────
                    SEARCH BAR
                    ──────────────────────────────────────────────────────────── */}
                <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder="Search sponsorships..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                            flex: 1,
                            minWidth: 250,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                backgroundColor: palette.paper,
                                backdropFilter: 'blur(8px)',
                                border: `1px solid ${palette.hairline}`,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    borderColor: palette.rust,
                                    backgroundColor: palette.paper,
                                    boxShadow: `0 4px 12px ${palette.rust}15`,
                                },
                                '&.Mui-focused': {
                                    borderColor: palette.rust,
                                    backgroundColor: palette.paper,
                                    boxShadow: `0 4px 16px ${palette.rust}20`,
                                }
                            },
                        }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: palette.inkLight, fontSize: 18 }} />
                                    </InputAdornment>
                                ),
                            }
                        }}
                    />
                    <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={fetchSponsorships}
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: palette.hairline,
                                color: palette.ink,
                                backdropFilter: 'blur(4px)',
                                '&:hover': { borderColor: palette.rust, bgcolor: `${palette.rust}08` }
                            }}
                        >
                            Refresh
                        </Button>
                    </motion.div>
                </Box>

                {/* ────────────────────────────────────────────────────────────
                    MAIN GLASS CARD WITH TABS
                    ──────────────────────────────────────────────────────────── */}
                <GlassCard accent={palette.rust} delay={0.1} hover={false} elevation="lg" sx={{ p: 0 }}>
                    <Box sx={{ borderBottom: `1px solid ${palette.hairline}`, px: { xs: 1, sm: 2 }, pt: 1, overflowX: 'auto' }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                minHeight: 56,
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                    py: 1.5,
                                    minHeight: 48,
                                    minWidth: 'auto',
                                    px: { xs: 1.5, sm: 2.5 },
                                    borderRadius: '10px 10px 0 0',
                                    transition: 'all 0.3s ease',
                                    color: palette.inkLight,
                                    fontFamily: fontBody,
                                    '&:hover': {
                                        color: palette.ink,
                                        bgcolor: 'rgba(26,21,18,0.02)',
                                    },
                                    '&.Mui-selected': {
                                        color: palette.ink,
                                    }
                                },
                                '& .MuiTabs-indicator': {
                                    height: 3,
                                    borderRadius: '3px',
                                    backgroundColor: tabs[tabValue]?.color || palette.rust,
                                }
                            }}
                        >
                            {tabs.map((tab, index) => (
                                <Tab
                                    key={index}
                                    icon={React.cloneElement(tab.icon, {
                                        sx: { fontSize: { xs: 16, sm: 18 }, color: tabValue === index ? tab.color : palette.inkLight }
                                    })}
                                    label={tab.label}
                                    iconPosition="start"
                                />
                            ))}
                        </Tabs>
                    </Box>

                    <Box sx={{ p: { xs: 2, sm: 3 } }}>
                        {/* All Sponsorships Tab */}
                        <TabPanel value={tabValue} index={0}>
                            {renderSponsorshipTable()}
                        </TabPanel>

                        {/* Active Tab */}
                        <TabPanel value={tabValue} index={1}>
                            {renderSponsorshipTable()}
                        </TabPanel>

                        {/* Pending Tab */}
                        <TabPanel value={tabValue} index={2}>
                            {renderSponsorshipTable()}
                        </TabPanel>

                        {/* Expired Tab */}
                        <TabPanel value={tabValue} index={3}>
                            {renderSponsorshipTable()}
                        </TabPanel>

                        {/* Expiring Soon Tab */}
                        <TabPanel value={tabValue} index={4}>
                            {renderSponsorshipTable()}
                        </TabPanel>
                    </Box>
                </GlassCard>

                {/* ────────────────────────────────────────────────────────────
                    ADD/EDIT DIALOG
                    ──────────────────────────────────────────────────────────── */}
                <Dialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    maxWidth="sm"
                    fullWidth
                    slotProps={{
                        paper: {
                            sx: {
                                ...liquidGlass(palette.rust, 0.08, 'lg'),
                                borderRadius: '24px',
                                overflow: 'hidden',
                                '&::after': sheenLayer(palette.rust),
                            }
                        }
                    }}
                >
                    <DialogTitle sx={{
                        borderBottom: `1px solid ${palette.hairline}`,
                        pb: 2,
                        fontFamily: fontDisplay,
                        fontWeight: 600,
                        color: palette.ink,
                        background: `linear-gradient(135deg, ${palette.canvasAlt}, ${palette.paper})`,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {dialogMode === 'add' ? (
                                <AddIcon sx={{ color: palette.rust }} />
                            ) : (
                                <EditIcon sx={{ color: palette.gold }} />
                            )}
                            {dialogMode === 'add' ? 'Create Sponsorship' : 'Edit Sponsorship'}
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Form fields remain the same as original */}
                            <FormControl fullWidth>
                                <InputLabel sx={{ color: palette.inkSoft }}>Company</InputLabel>
                                <Select
                                    value={formData.companyId}
                                    onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                                    label="Company"
                                    sx={{
                                        borderRadius: '12px',
                                        backgroundColor: palette.paper,
                                        backdropFilter: 'blur(8px)',
                                        border: `1px solid ${palette.hairline}`,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: palette.rust,
                                            backgroundColor: palette.paper,
                                            boxShadow: `0 4px 12px ${palette.rust}15`,
                                        },
                                        '&.Mui-focused': {
                                            borderColor: palette.rust,
                                            backgroundColor: palette.paper,
                                            boxShadow: `0 4px 16px ${palette.rust}20`,
                                        }
                                    }}
                                >
                                    {companies.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.companyName || 'Unnamed Company'}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel sx={{ color: palette.inkSoft }}>Package Type</InputLabel>
                                <Select
                                    value={formData.packageType}
                                    onChange={(e) => handlePackageChange(e.target.value)}
                                    label="Package Type"
                                    sx={{
                                        borderRadius: '12px',
                                        backgroundColor: palette.paper,
                                        backdropFilter: 'blur(8px)',
                                        border: `1px solid ${palette.hairline}`,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: palette.rust,
                                            backgroundColor: palette.paper,
                                            boxShadow: `0 4px 12px ${palette.rust}15`,
                                        },
                                        '&.Mui-focused': {
                                            borderColor: palette.rust,
                                            backgroundColor: palette.paper,
                                            boxShadow: `0 4px 16px ${palette.rust}20`,
                                        }
                                    }}
                                >
                                    {packageOptions.map((p) => (
                                        <MenuItem key={p.value} value={p.value}>
                                            {p.label} ({p.displayDuration})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Rest of form fields continue... */}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ borderTop: `1px solid ${palette.hairline}`, p: 2.5, gap: 1 }}>
                        <Button
                            onClick={() => setOpenDialog(false)}
                            sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={handleSaveSponsorship}
                                startIcon={dialogMode === 'add' ? <AddIcon /> : <SaveIcon />}
                                sx={{
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1.1,
                                    background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                    boxShadow: `0 6px 16px -8px rgba(199,91,46,0.45)`,
                                    '&:hover': {
                                        boxShadow: `0 8px 24px -8px rgba(199,91,46,0.55)`,
                                        transform: 'translateY(-2px)',
                                    }
                                }}
                            >
                                {dialogMode === 'add' ? 'Create Sponsorship' : 'Save Changes'}
                            </Button>
                        </motion.div>
                    </DialogActions>
                </Dialog>

                {/* ────────────────────────────────────────────────────────────
                    MANUAL ASSIGN DIALOG
                    ──────────────────────────────────────────────────────────── */}
                {/* ────────────────────────────────────────────────────────────
    MANUAL ASSIGN DIALOG - CORRECTED
    ──────────────────────────────────────────────────────────── */}
<Dialog
    open={openManualAssign}
    onClose={() => setOpenManualAssign(false)}
    maxWidth="sm"
    fullWidth
    slotProps={{
        paper: {
            sx: {
                ...liquidGlass(palette.teal, 0.08, 'lg'),
                borderRadius: '24px',
                overflow: 'hidden',
                '&::after': sheenLayer(palette.teal),
            }
        }
    }}
>
    <DialogTitle sx={{
        borderBottom: `1px solid ${palette.hairline}`,
        pb: 2,
        fontFamily: fontDisplay,
        fontWeight: 600,
        color: palette.ink,
        background: `linear-gradient(135deg, ${palette.canvasAlt}, ${palette.paper})`,
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PersonAddIcon sx={{ color: palette.teal }} />
            Manual Subscription Assignment
        </Box>
    </DialogTitle>
    <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
                <InputLabel sx={{ color: palette.inkSoft }}>Select User</InputLabel>
                <Select
                    value={manualAssignData.userId}
                    onChange={(e) => setManualAssignData({ ...manualAssignData, userId: e.target.value })}
                    label="Select User"
                    sx={{
                        borderRadius: '12px',
                        backgroundColor: palette.paper,
                        backdropFilter: 'blur(8px)',
                        border: `1px solid ${palette.hairline}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            borderColor: palette.rust,
                            backgroundColor: palette.paper,
                            boxShadow: `0 4px 12px ${palette.rust}15`,
                        },
                        '&.Mui-focused': {
                            borderColor: palette.rust,
                            backgroundColor: palette.paper,
                            boxShadow: `0 4px 16px ${palette.rust}20`,
                        }
                    }}
                >
                    {users.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                            {u.displayName || u.email} ({u.email})
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl fullWidth>
                <InputLabel sx={{ color: palette.inkSoft }}>Select Package</InputLabel>
                <Select
                    value={manualAssignData.packageId}
                    onChange={(e) => {
                        const pkg = packages.find(p => p.id === e.target.value);
                        setManualAssignData({
                            ...manualAssignData,
                            packageId: e.target.value,
                            amount: pkg?.price || 0
                        });
                    }}
                    label="Select Package"
                    sx={{
                        borderRadius: '12px',
                        backgroundColor: palette.paper,
                        backdropFilter: 'blur(8px)',
                        border: `1px solid ${palette.hairline}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            borderColor: palette.rust,
                            backgroundColor: palette.paper,
                            boxShadow: `0 4px 12px ${palette.rust}15`,
                        },
                        '&.Mui-focused': {
                            borderColor: palette.rust,
                            backgroundColor: palette.paper,
                            boxShadow: `0 4px 16px ${palette.rust}20`,
                        }
                    }}
                >
                    {packages.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                            {p.name || p.packageName || 'Package'} - ${p.price || p.amount || 0} ({p.duration || p.validity || 30} days)
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    value={manualAssignData.amount}
                    onChange={(e) => setManualAssignData({ ...manualAssignData, amount: parseFloat(e.target.value) || 0 })}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: palette.paper,
                            backdropFilter: 'blur(8px)',
                            border: `1px solid ${palette.hairline}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                borderColor: palette.rust,
                                backgroundColor: palette.paper,
                                boxShadow: `0 4px 12px ${palette.rust}15`,
                            },
                            '&.Mui-focused': {
                                borderColor: palette.rust,
                                backgroundColor: palette.paper,
                                boxShadow: `0 4px 16px ${palette.rust}20`,
                            }
                        }
                    }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <MoneyIcon sx={{ color: palette.pine }} />
                                </InputAdornment>
                            ),
                        }
                    }}
                />
                <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={manualAssignData.startDate}
                    onChange={(e) => setManualAssignData({ ...manualAssignData, startDate: e.target.value })}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: palette.paper,
                            backdropFilter: 'blur(8px)',
                            border: `1px solid ${palette.hairline}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                borderColor: palette.rust,
                                backgroundColor: palette.paper,
                                boxShadow: `0 4px 12px ${palette.rust}15`,
                            },
                            '&.Mui-focused': {
                                borderColor: palette.rust,
                                backgroundColor: palette.paper,
                                boxShadow: `0 4px 16px ${palette.rust}20`,
                            }
                        }
                    }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CalendarTodayIcon sx={{ color: palette.teal }} />
                                </InputAdornment>
                            ),
                        },
                        inputLabel: { shrink: true }
                    }}
                />
            </Box>

            <TextField
                fullWidth
                label="End Date"
                type="date"
                value={manualAssignData.endDate}
                onChange={(e) => setManualAssignData({ ...manualAssignData, endDate: e.target.value })}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: palette.paper,
                        backdropFilter: 'blur(8px)',
                        border: `1px solid ${palette.hairline}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            borderColor: palette.rust,
                            backgroundColor: palette.paper,
                            boxShadow: `0 4px 12px ${palette.rust}15`,
                        },
                        '&.Mui-focused': {
                            borderColor: palette.rust,
                            backgroundColor: palette.paper,
                            boxShadow: `0 4px 16px ${palette.rust}20`,
                        }
                    }
                }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <CalendarTodayIcon sx={{ color: palette.clay }} />
                            </InputAdornment>
                        ),
                    },
                    inputLabel: { shrink: true }
                }}
            />

            <TextField
                fullWidth
                label="Notes"
                value={manualAssignData.notes}
                onChange={(e) => setManualAssignData({ ...manualAssignData, notes: e.target.value })}
                multiline
                rows={2}
                placeholder="Reason for manual assignment"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: palette.paper,
                        backdropFilter: 'blur(8px)',
                        border: `1px solid ${palette.hairline}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            borderColor: palette.rust,
                            backgroundColor: palette.paper,
                            boxShadow: `0 4px 12px ${palette.rust}15`,
                        },
                        '&.Mui-focused': {
                            borderColor: palette.rust,
                            backgroundColor: palette.paper,
                            boxShadow: `0 4px 16px ${palette.rust}20`,
                        }
                    }
                }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <AssignmentIcon sx={{ color: palette.inkSoft }} />
                            </InputAdornment>
                        ),
                    }
                }}
            />

            <Alert severity="info" sx={{ borderRadius: '12px', bgcolor: `${palette.teal}08`, border: `1px solid ${palette.teal}20` }}>
                <Typography variant="caption" sx={{ color: palette.ink }}>
                    This will manually assign a sponsorship to the selected user. The subscription will appear on their profile and the home page.
                </Typography>
            </Alert>
        </Box>
    </DialogContent>
    <DialogActions sx={{ borderTop: `1px solid ${palette.hairline}`, p: 2.5, gap: 1 }}>
        <Button
            onClick={() => setOpenManualAssign(false)}
            sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'none' }}
        >
            Cancel
        </Button>
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
                variant="contained"
                disableElevation
                onClick={handleManualAssign}
                startIcon={<SendIcon />}
                sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.1,
                    background: `linear-gradient(135deg, ${palette.teal}, ${palette.tealLight})`,
                    boxShadow: `0 6px 16px -8px rgba(26,107,115,0.45)`,
                    '&:hover': {
                        boxShadow: `0 8px 24px -8px rgba(26,107,115,0.55)`,
                        transform: 'translateY(-2px)',
                    }
                }}
            >
                Assign Subscription
            </Button>
        </motion.div>
    </DialogActions>
</Dialog>

                {/* ────────────────────────────────────────────────────────────
                    PAYMENT DETAILS DIALOG
                    ──────────────────────────────────────────────────────────── */}
                <Dialog
                    open={openPaymentDetails}
                    onClose={() => setOpenPaymentDetails(false)}
                    maxWidth="md"
                    fullWidth
                    slotProps={{
                        paper: {
                            sx: {
                                ...liquidGlass(palette.gold, 0.08, 'lg'),
                                borderRadius: '24px',
                                overflow: 'hidden',
                                '&::after': sheenLayer(palette.gold),
                            }
                        }
                    }}
                >
                    <DialogTitle sx={{
                        borderBottom: `1px solid ${palette.hairline}`,
                        pb: 2,
                        fontFamily: fontDisplay,
                        fontWeight: 600,
                        color: palette.ink,
                        background: `linear-gradient(135deg, ${palette.canvasAlt}, ${palette.paper})`,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <ReceiptIcon sx={{ color: palette.gold }} />
                            Payment Details
                            {selectedPayment && (
                                <Chip
                                    label={`#${selectedPayment.sponsorshipId?.slice(-6) || 'N/A'}`}
                                    size="small"
                                    sx={{ ml: 1, bgcolor: `${palette.rust}12`, color: palette.rust, fontWeight: 600, backdropFilter: 'blur(4px)' }}
                                />
                            )}
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        {selectedPayment && (
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <GlassCard accent={palette.rust} hover={false} elevation="sm" sx={{ p: 2 }}>
                                        <Typography variant="caption" sx={{ color: palette.inkSoft, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            Customer Information
                                        </Typography>
                                        <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink, mt: 0.5 }}>
                                            {selectedPayment.userName || getCompanyName(selectedPayment.companyId)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: palette.inkSoft }}>
                                            {selectedPayment.userEmail || 'No email provided'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: palette.inkSoft, display: 'block', mt: 0.5 }}>
                                            Company: {getCompanyName(selectedPayment.companyId)}
                                        </Typography>
                                    </GlassCard>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <GlassCard accent={palette.gold} hover={false} elevation="sm" sx={{ p: 2 }}>
                                        <Typography variant="caption" sx={{ color: palette.inkSoft, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            Package Details
                                        </Typography>
                                        <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink, mt: 0.5 }}>
                                            {selectedPayment.packageType || 'Premium'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: palette.inkSoft }}>
                                            Amount: ${selectedPayment.amount || 0} {selectedPayment.currency || 'USD'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: palette.inkSoft, display: 'block', mt: 0.5 }}>
                                            Period: {formatDate(selectedPayment.startDate)} - {formatDate(selectedPayment.endDate)}
                                        </Typography>
                                    </GlassCard>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <GlassCard accent={palette.pine} hover={false} elevation="sm" sx={{ p: 2 }}>
                                        <Typography variant="caption" sx={{ color: palette.inkSoft, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            Payment Status
                                        </Typography>
                                        <Box sx={{ mt: 1 }}>
                                            <PaymentStatusChip status={selectedPayment.paymentStatus} />
                                        </Box>
                                    </GlassCard>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <GlassCard accent={palette.teal} hover={false} elevation="sm" sx={{ p: 2 }}>
                                        <Typography variant="caption" sx={{ color: palette.inkSoft, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            Payment Method
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600} sx={{ color: palette.ink, mt: 0.5 }}>
                                            {selectedPayment.paymentMethod || 'Not specified'}
                                        </Typography>
                                    </GlassCard>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <GlassCard accent={palette.purple} hover={false} elevation="sm" sx={{ p: 2 }}>
                                        <Typography variant="caption" sx={{ color: palette.inkSoft, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            Transaction ID
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600} sx={{ color: palette.ink, mt: 0.5 }}>
                                            {selectedPayment.transactionId || 'N/A'}
                                        </Typography>
                                    </GlassCard>
                                </Grid>
                                {selectedPayment.notes && (
                                    <Grid item xs={12}>
                                        <GlassCard accent={palette.inkLight} hover={false} elevation="sm" sx={{ p: 2 }}>
                                            <Typography variant="caption" sx={{ color: palette.inkSoft, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                Notes
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: palette.ink, mt: 0.5 }}>
                                                {selectedPayment.notes}
                                            </Typography>
                                        </GlassCard>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<PrintIcon />}
                                            sx={{
                                                borderRadius: '12px',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                borderColor: palette.hairline,
                                                color: palette.ink,
                                                backdropFilter: 'blur(4px)',
                                                '&:hover': { borderColor: palette.rust, bgcolor: `${palette.rust}08` }
                                            }}
                                        >
                                            Print Receipt
                                        </Button>
                                        <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                                            <Button
                                                variant="contained"
                                                disableElevation
                                                startIcon={<DownloadIcon />}
                                                sx={{
                                                    borderRadius: '12px',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                                    boxShadow: `0 6px 16px -8px rgba(199,91,46,0.45)`,
                                                    '&:hover': {
                                                        boxShadow: `0 8px 24px -8px rgba(199,91,46,0.55)`,
                                                        transform: 'translateY(-2px)',
                                                    }
                                                }}
                                            >
                                                Download Receipt
                                            </Button>
                                        </motion.div>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ borderTop: `1px solid ${palette.hairline}`, p: 2.5, gap: 1 }}>
                        <Button
                            onClick={() => setOpenPaymentDetails(false)}
                            sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'none' }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ────────────────────────────────────────────────────────────
                    DELETE CONFIRMATION
                    ──────────────────────────────────────────────────────────── */}
                <Dialog
                    open={deleteConfirm.open}
                    onClose={() => setDeleteConfirm({ open: false, id: null })}
                    maxWidth="xs"
                    fullWidth
                    slotProps={{
                        paper: {
                            sx: {
                                ...liquidGlass(palette.clay, 0.08, 'lg'),
                                borderRadius: '24px',
                                overflow: 'hidden',
                                '&::after': sheenLayer(palette.clay),
                            }
                        }
                    }}
                >
                    <DialogTitle sx={{
                        fontFamily: fontDisplay,
                        fontWeight: 600,
                        color: palette.clay,
                        background: `linear-gradient(135deg, ${palette.canvasAlt}, ${palette.paper})`,
                    }}>
                        <DeleteIcon sx={{ color: palette.clay, mr: 1, verticalAlign: 'middle' }} />
                        Delete Sponsorship
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ color: palette.ink, fontFamily: fontBody }}>
                            Are you sure you want to delete this sponsorship? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ borderTop: `1px solid ${palette.hairline}`, p: 2.5, gap: 1 }}>
                        <Button
                            onClick={() => setDeleteConfirm({ open: false, id: null })}
                            sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={handleDeleteSponsorship}
                                startIcon={<DeleteIcon />}
                                sx={{
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1.1,
                                    bgcolor: palette.clay,
                                    '&:hover': {
                                        bgcolor: palette.clay,
                                        opacity: 0.8,
                                        transform: 'translateY(-2px)',
                                    }
                                }}
                            >
                                Delete
                            </Button>
                        </motion.div>
                    </DialogActions>
                </Dialog>
            </Container>

            {/* ────────────────────────────────────────────────────────────
                GLOBAL STYLES
                ──────────────────────────────────────────────────────────── */}
            <style>
                {`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; transform: scale(1); }
                        50% { opacity: 0.5; transform: scale(0.8); }
                    }
                    @keyframes shimmerRule {
                        0%, 100% { opacity: 0.7; width: 40px; }
                        50% { opacity: 1; width: 56px; }
                    }
                    @keyframes grainDrift {
                        0% { transform: translate(0, 0); }
                        20% { transform: translate(-2%, 2%); }
                        40% { transform: translate(2%, -1%); }
                        60% { transform: translate(-1%, -2%); }
                        80% { transform: translate(1%, 1%); }
                        100% { transform: translate(0, 0); }
                    }
                    * {
                        scrollbar-width: thin;
                        scrollbar-color: ${palette.hairline} transparent;
                    }
                    ::-webkit-scrollbar {
                        width: 6px;
                        height: 6px;
                    }
                    ::-webkit-scrollbar-track {
                        background: transparent;
                    }
                    ::-webkit-scrollbar-thumb {
                        background: ${palette.hairline};
                        border-radius: 3px;
                    }
                    ::-webkit-scrollbar-thumb:hover {
                        background: ${palette.hairlineStrong};
                    }
                    .MuiTabs-scrollButtons {
                        color: ${palette.inkLight};
                    }
                    .MuiTabs-scrollButtons:hover {
                        color: ${palette.rust};
                    }
                `}
            </style>
        </Box>
    );
};

export default Sponsorships;